import { DESPEDIDA_PADRAO, type ConversaDetalhe, type EncerrarConversa } from '@mymaxx/shared';
import { Aviso, Botao, Modal } from '@mymaxx/ui';
import { Star } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ErroApi } from '../lib/api';
import { atendimentoApi } from '../lib/atendimento-api';
import { useSessao } from '../lib/sessao';

/**
 * Transferir e encerrar: os dois diálogos que a caixa de entrada do painel e a estação
 * do atendente compartilham, para as duas telas se comportarem igual.
 */
export function ModalTransferir({
  conversa,
  setores,
  onFechar,
  onPronto,
}: {
  conversa: ConversaDetalhe;
  setores: { id: string; nome: string }[];
  onFechar: () => void;
  onPronto: () => void;
}) {
  const [setorId, setSetorId] = useState('');
  const [atendenteId, setAtendenteId] = useState('');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  // A equipe do setor escolhido, para poder entregar na mão de alguém em vez de devolver
  // para a fila. Só busca depois da escolha do setor.
  const equipe = useQuery({
    queryKey: ['equipe-do-setor', setorId],
    queryFn: () => atendimentoApi.equipeDoSetor(setorId),
    enabled: Boolean(setorId),
  });

  // Quem está de plantão e ainda tem vaga. Offline e em pausa ficam de fora: escolher
  // alguém que não está lá é a mesma coisa que deixar o cliente esperando.
  const disponiveis = (equipe.data ?? []).filter(
    (a) => a.status === 'online' && a.usuarioId !== conversa.atendente?.id,
  );

  const transferir = useMutation({
    mutationFn: () =>
      atendimentoApi.transferir(conversa.id, {
        setorId,
        ...(atendenteId ? { atendenteId } : {}),
        motivo,
      }),
    onSuccess: onPronto,
    onError: (e) => setErro(e instanceof ErroApi ? e.mensagem : 'Falha ao transferir'),
  });

  return (
    <Modal aberto titulo="Transferir atendimento" onFechar={onFechar}>
      <div className="space-y-3">
        <label className="block text-sm font-medium">
          Para o setor
          <select
            value={setorId}
            onChange={(e) => {
              setSetorId(e.target.value);
              setAtendenteId('');
            }}
            className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
          >
            <option value="">Selecione…</option>
            {/* O próprio setor fica na lista: passar o atendimento para um colega da
                mesma equipe é o caso mais comum, e antes era o único impossível. */}
            {setores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nome}
                {s.id === conversa.setor?.id ? ' (setor atual)' : ''}
              </option>
            ))}
          </select>
        </label>
        {setorId && (
          <label className="block text-sm font-medium">
            Para quem (opcional)
            <select
              value={atendenteId}
              onChange={(e) => setAtendenteId(e.target.value)}
              className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
            >
              <option value="">Qualquer um do setor (vai para a fila)</option>
              {disponiveis.map((a) => (
                <option key={a.usuarioId} value={a.usuarioId}>
                  {a.nome} — {a.ativas}/{a.limite}
                </option>
              ))}
            </select>
            {equipe.isSuccess && disponiveis.length === 0 && (
              <span className="mt-1 block text-xs text-neutral-500">
                Ninguém disponível nesse setor agora; vai para a fila.
              </span>
            )}
          </label>
        )}
        <label className="block text-sm font-medium">
          Motivo (o cliente não vê)
          <input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
          />
        </label>
        <p className="text-xs text-neutral-500">
          O protocolo continua o mesmo. Sem escolher a pessoa, o cliente volta para a fila do setor.
        </p>
        {erro && <Aviso tipo="erro">{erro}</Aviso>}
        <div className="flex justify-end gap-2">
          <Botao variante="secundario" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao
            disabled={!setorId}
            carregando={transferir.isPending}
            onClick={() => transferir.mutate()}
          >
            Transferir
          </Botao>
        </div>
      </div>
    </Modal>
  );
}

export function ModalEncerrar({
  conversa,
  motivos,
  onFechar,
  onPronto,
}: {
  conversa: ConversaDetalhe;
  motivos: { id: string; nome: string }[];
  onFechar: () => void;
  onPronto: (escopo: 'todos' | 'apenas_eu') => void;
}) {
  const [motivoTagId, setMotivo] = useState('');
  // A despedida de quem está encerrando, se ele escreveu a dele em "Minhas respostas".
  const minhaDespedida = useSessao((e) => e.usuario?.despedida);
  const [despedida, setDespedida] = useState(minhaDespedida || DESPEDIDA_PADRAO);
  const [escopo, setEscopo] = useState<'todos' | 'apenas_eu'>('todos');
  const [notaTecnico, setNotaTecnico] = useState(0);
  const [comentarioTecnico, setComentarioTecnico] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const meuId = useSessao((e) => e.usuario?.id);
  // Com mais gente dentro, encerrar tem dois sentidos e a escolha precisa ser explícita.
  const acompanhado = conversa.participantes.length > 1;

  const encerrar = useMutation({
    mutationFn: () => {
      const dados: EncerrarConversa = { escopo, despedida: escopo === 'todos' ? despedida : '' };
      if (motivoTagId && escopo === 'todos') dados.motivoTagId = motivoTagId;
      if (notaTecnico && escopo === 'todos') {
        dados.avaliacaoTecnico = notaTecnico;
        if (comentarioTecnico.trim()) dados.avaliacaoTecnicoNota = comentarioTecnico.trim();
      }
      return atendimentoApi.encerrar(conversa.id, dados);
    },
    onSuccess: () => onPronto(escopo),
    onError: (e) => setErro(e instanceof ErroApi ? e.mensagem : 'Falha ao encerrar'),
  });

  return (
    <Modal aberto titulo="Encerrar atendimento" onFechar={onFechar}>
      <div className="space-y-3">
        {acompanhado && (
          <fieldset className="space-y-2 rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
            <legend className="px-1 text-xs font-medium text-neutral-500">
              Tem mais gente neste atendimento
            </legend>
            {(
              [
                [
                  'todos',
                  'Encerrar para todos',
                  'Fecha o protocolo. O cliente recebe a despedida e o convite para avaliar.',
                ],
                [
                  'apenas_eu',
                  'Só eu saio',
                  `${outrosNomes(conversa, meuId)} continua(m) com o cliente.`,
                ],
              ] as const
            ).map(([valor, titulo, ajuda]) => (
              <label key={valor} className="flex cursor-pointer gap-2 text-sm">
                <input
                  type="radio"
                  name="escopo"
                  className="mt-1"
                  checked={escopo === valor}
                  onChange={() => setEscopo(valor)}
                />
                <span>
                  <span className="font-medium">{titulo}</span>
                  <span className="block text-xs text-neutral-500">{ajuda}</span>
                </span>
              </label>
            ))}
          </fieldset>
        )}

        {escopo === 'apenas_eu' ? (
          <p className="text-xs text-neutral-500">
            O atendimento continua aberto no mesmo protocolo. Fica registrado no histórico que você
            saiu.
          </p>
        ) : (
          <>
            <label className="block text-sm font-medium">
              Motivo do atendimento
              <select
                value={motivoTagId}
                onChange={(e) => setMotivo(e.target.value)}
                className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              >
                <option value="">Sem motivo</option>
                {motivos.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nome}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-medium">
              Mensagem de despedida
              <textarea
                rows={3}
                value={despedida}
                onChange={(e) => setDespedida(e.target.value)}
                className="mt-1 w-full resize-none rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              />
            </label>
            {/* Liberação de sinal avalia nos dois sentidos. A nota não trava o
                encerramento: obrigar num dia corrido só produziria nota no automático. */}
            {conversa.clienteTecnico && (
              <div className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-700">
                <p className="text-sm font-medium">
                  Como foi o técnico?{' '}
                  <span className="font-normal text-neutral-500">(opcional)</span>
                </p>
                <div className="mt-1 flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      aria-label={`${n} estrela${n > 1 ? 's' : ''}`}
                      onClick={() => setNotaTecnico(n === notaTecnico ? 0 : n)}
                      className={n <= notaTecnico ? 'text-amber-400' : 'text-neutral-300'}
                    >
                      <Star size={22} className={n <= notaTecnico ? 'fill-amber-400' : ''} />
                    </button>
                  ))}
                </div>
                <input
                  value={comentarioTecnico}
                  onChange={(e) => setComentarioTecnico(e.target.value)}
                  placeholder="Algo a registrar? (o técnico não vê)"
                  className="mt-2 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                />
              </div>
            )}
            <p className="text-xs text-neutral-500">
              Depois de encerrar, o cliente recebe o convite para avaliar o atendimento.
            </p>
          </>
        )}
        {erro && <Aviso tipo="erro">{erro}</Aviso>}
        <div className="flex justify-end gap-2">
          <Botao variante="secundario" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao
            variante="perigo"
            carregando={encerrar.isPending}
            onClick={() => encerrar.mutate()}
          >
            {escopo === 'apenas_eu' ? 'Sair do atendimento' : 'Encerrar'}
          </Botao>
        </div>
      </div>
    </Modal>
  );
}

/** Quem fica com o cliente quando eu saio — usado no texto da escolha. */
function outrosNomes(conversa: ConversaDetalhe, meuId: string | undefined): string {
  const nomes = conversa.participantes
    .filter((p) => p.usuarioId !== meuId)
    .map((p) => p.nome.split(/\s+/)[0] ?? p.nome);
  return nomes.length > 0 ? nomes.join(', ') : 'Quem ficar';
}
